import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArcButton } from "@/components/ArcButton";
import { LabelBadge } from "@/components/LabelBadge";
import { Input } from "@/components/ui/input";
import { Award, RotateCcw, Share2, Link2, Trophy, ArrowLeft, Crown } from "lucide-react";
import { toast } from "sonner";

function levelFor(quiz, score) {
  const sorted = [...(quiz.levels || [])].sort((a, b) => a.min - b.min);
  let result = sorted[0] || null;
  for (const l of sorted) if (score >= l.min) result = l;
  return result;
}

function getVisitorId() {
  let id = localStorage.getItem("arc_visitor");
  if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()); localStorage.setItem("arc_visitor", id); }
  return id;
}

export default function QuizPlay() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [quiz, setQuiz] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [started, setStarted] = useState(false);
  const [name, setName] = useState("");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);
  const [board, setBoard] = useState(null);
  const [nameError, setNameError] = useState("");
  const [savedName, setSavedName] = useState("");
  const recorded = useRef(false);

  const sharedLevel = params.get("level") ? parseInt(params.get("level")) : null;

  useEffect(() => {
    let alive = true;
    api.get(`/quizzes/${slug}`).then((r) => { if (alive) setQuiz(r.data); }).catch(() => { if (alive) setNotFound(true); });
    api.get(`/quizzes/${slug}/stats`).then((r) => { if (alive) setBoard(r.data); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const questions = quiz?.questions || [];
  const total = answers.reduce((a, b) => a + b, 0);
  const accent = quiz?.encyclopedia === "zyfley" ? "var(--zyfley)" : "var(--ghoulax)";
  const btnVariant = quiz?.encyclopedia === "zyfley" ? "zyfley" : "ghoulax";
  const tint = quiz?.encyclopedia === "zyfley" ? "var(--zyfley-soft)" : "var(--ghoulax-soft)";

  const result = quiz
    ? (sharedLevel ? ((quiz.levels || []).find((l) => l.level === sharedLevel) || levelFor(quiz, 0)) : levelFor(quiz, total))
    : null;

  const recordResult = (nameToUse) => {
    if (!quiz || !result) return;
    setNameError("");
    api.post(`/quizzes/${slug}/record`, { level: result.level, visitor_id: getVisitorId(), name: nameToUse || "" })
      .then((r) => { setBoard(r.data); setSavedName((nameToUse || "").trim()); })
      .catch((e) => {
        if (e.response?.status === 409) {
          setNameError(e.response?.data?.detail || "That name is already taken. Please choose another.");
        }
      });
  };

  useEffect(() => {
    if (done && quiz && result && !sharedLevel && !recorded.current) {
      recorded.current = true;
      recordResult(name);
    }
  }, [done, quiz, result, sharedLevel]); // eslint-disable-line

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="font-serif-arc text-2xl font-black">Quiz not found</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--arc-text-2)" }}>This quiz may have been removed or unpublished.</p>
        <div className="mt-6"><ArcButton to="/quizzes" variant="outline"><ArrowLeft size={15} /> Back to all quizzes</ArcButton></div>
      </div>
    );
  }
  if (!quiz) return <div className="mx-auto max-w-2xl px-4 py-20 text-center text-sm" style={{ color: "var(--arc-text-2)" }}>Loading quiz…</div>;

  const answer = (p) => {
    const next = [...answers, p];
    setAnswers(next);
    if (step + 1 < questions.length) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => { setStep(0); setAnswers([]); setDone(false); setStarted(false); setNameError(""); setSavedName(""); recorded.current = false; setParams({}); };
  const beginQuiz = (withName) => { setName(withName ? name.trim() : ""); setStarted(true); };

  const shareLink = `${window.location.origin}/quiz/${slug}?level=${result?.level}`;
  const shareText = `I scored Level ${result?.level} — ${result?.name} on ${quiz.title}. Where do you land?`;
  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Share link copied to clipboard"); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: quiz.title, text: shareText, url: shareLink }); } catch (e) { /* user dismissed the share sheet */ } }
    else { navigator.clipboard.writeText(`${shareText} ${shareLink}`); toast.success("Result copied to clipboard"); }
  };

  const showResult = done || sharedLevel;
  const showNameEntry = !started && !showResult;
  const maxScore = questions.reduce((sum, q) => sum + Math.max(0, ...q.options.map((o) => o.p)), 0);

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
      <div className="text-center">
        <Link to="/quizzes" className="mb-4 inline-flex items-center gap-1 text-xs hover:underline" style={{ color: "var(--arc-text-2)" }} data-testid="quiz-back-hub"><ArrowLeft size={13} /> All quizzes</Link>
        <h1 className="font-serif-arc text-3xl font-black tracking-tight sm:text-4xl" style={{ color: accent }} data-testid="quiz-title">{quiz.title}</h1>
        {quiz.subtitle && <p className="mt-3 text-sm italic" style={{ color: "var(--arc-text-2)" }}>{quiz.subtitle}</p>}
        {quiz.label && <div className="mt-3 flex justify-center"><LabelBadge label={quiz.label} /></div>}
      </div>

      {/* Name entry */}
      {showNameEntry && (
        <div className="mt-10 rounded-md border p-6 shadow-sm arc-fade-up" style={{ backgroundColor: "var(--arc-card)" }} data-testid="quiz-name-entry">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: tint }}>
            <Trophy size={26} style={{ color: accent }} />
          </div>
          <h2 className="text-center font-serif-arc text-xl font-bold">Claim your spot on the leaderboard</h2>
          <p className="mx-auto mt-2 max-w-md text-center text-sm" style={{ color: "var(--arc-text-2)" }}>
            Enter a name to show off your result, or skip to play anonymously.
          </p>
          <div className="mx-auto mt-5 max-w-sm">
            <Input
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) beginQuiz(true); }}
              placeholder="Your name (optional)"
              data-testid="quiz-name-input"
              style={{ backgroundColor: "var(--arc-bg)" }}
            />
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <ArcButton variant={btnVariant} onClick={() => beginQuiz(true)} disabled={!name.trim()} data-testid="quiz-start-named">Start quiz</ArcButton>
              <ArcButton variant="ghost" className="border" onClick={() => beginQuiz(false)} data-testid="quiz-start-anon">Skip / play anonymously</ArcButton>
            </div>
          </div>
        </div>
      )}

      {/* Questions */}
      {started && !showResult && (
        questions.length === 0 ? (
          <div className="mt-10 rounded-md border p-6 text-center text-sm" style={{ backgroundColor: "var(--arc-card)", color: "var(--arc-text-2)" }}>
            This quiz has no questions yet.
          </div>
        ) : (
        <div className="mt-10 rounded-md border p-6 shadow-sm arc-fade-up" style={{ backgroundColor: "var(--arc-card)" }} data-testid="quiz-question">
          <div className="mb-4 flex items-center justify-between text-xs" style={{ color: "var(--arc-text-2)" }}>
            <span>Question {step + 1} of {questions.length}</span>
            <span>{Math.round((step / questions.length) * 100)}%</span>
          </div>
          <div className="mb-5 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: "var(--arc-border)" }}>
            <div className="h-full transition-all" style={{ width: `${(step / questions.length) * 100}%`, backgroundColor: accent }} />
          </div>
          <h2 className="font-serif-arc text-xl font-bold">{questions[step].q}</h2>
          <div className="mt-5 space-y-3">
            {questions[step].options.map((o, i) => (
              <button
                key={o.id || i}
                data-testid={`quiz-option-${i}`}
                onClick={() => answer(o.p)}
                className="block w-full rounded-md border px-4 py-3 text-left text-sm transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md"
                style={{ backgroundColor: "var(--arc-bg)" }}
              >
                {o.t}
              </button>
            ))}
          </div>
        </div>
        )
      )}

      {/* Result */}
      {showResult && result && (
        <div className="mt-10 overflow-hidden rounded-md border-2 shadow-md arc-fade-up" style={{ backgroundColor: "var(--arc-card)", borderColor: accent }} data-testid="quiz-result">
          <div className="px-6 py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: tint }}>
              <Award size={32} style={{ color: accent }} />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: accent }}>Level {result.level}</p>
            <h2 className="mt-2 font-serif-arc text-3xl font-black">{result.name}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed" style={{ color: "var(--arc-text-2)" }}>{result.desc}</p>
            {!sharedLevel && maxScore > 0 && <p className="mt-4 text-xs" style={{ color: "var(--arc-text-2)" }}>Score: {total} / {maxScore}</p>}
            {!sharedLevel && savedName && <p className="mt-2 text-xs font-semibold" style={{ color: accent }}>Added to the leaderboard as “{savedName}”.</p>}
          </div>

          {/* Name taken — re-entry */}
          {!sharedLevel && nameError && (
            <div className="border-t px-6 py-4" data-testid="quiz-name-conflict">
              <p className="text-sm font-semibold text-red-600">{nameError}</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Pick a different name" data-testid="quiz-rename-input" style={{ backgroundColor: "var(--arc-bg)" }} />
                <ArcButton variant={btnVariant} onClick={() => recordResult(name)} disabled={!name.trim()} data-testid="quiz-rename-submit">Add to leaderboard</ArcButton>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 border-t p-4 sm:flex-row sm:justify-center">
            <ArcButton variant={btnVariant} onClick={share} data-testid="quiz-share"><Share2 size={15} /> Share my badge</ArcButton>
            <ArcButton variant="outline" onClick={copyLink} data-testid="quiz-copy-link"><Link2 size={15} /> Copy link</ArcButton>
            <ArcButton variant="ghost" className="border" onClick={reset} data-testid="quiz-retake"><RotateCcw size={15} /> {sharedLevel ? "Take the quiz" : "Retake"}</ArcButton>
          </div>
          {quiz.result_footer && (
            <p className="border-t px-4 py-3 text-center text-xs italic" style={{ color: "var(--arc-text-2)" }}>{quiz.result_footer}</p>
          )}
        </div>
      )}

      {/* Named leaderboard */}
      {board && (
        <div className="mt-8 rounded-md border p-6 shadow-sm" data-testid="quiz-leaderboard" style={{ backgroundColor: "var(--arc-card)" }}>
          <div className="mb-1 flex items-center gap-2">
            <Trophy size={18} style={{ color: accent }} />
            <h3 className="font-serif-arc text-lg font-bold">Leaderboard</h3>
          </div>
          <p className="mb-5 text-xs" style={{ color: "var(--arc-text-2)" }}>
            {board.total > 0
              ? `${board.named} name${board.named === 1 ? "" : "s"} on the board out of ${board.total} result${board.total === 1 ? "" : "s"}.`
              : "No results recorded yet. Be the first."}
          </p>
          {(!board.entries || board.entries.length === 0) ? (
            <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No named results yet — enter a name to claim the top spot.</p>
          ) : (
            <ol className="space-y-2">
              {board.entries.map((e, i) => {
                const isMine = savedName && e.name === savedName;
                return (
                  <li key={`${e.name}-${i}`} data-testid={`board-entry-${i}`} className="flex items-center justify-between gap-3 rounded-md border px-4 py-2.5" style={{ backgroundColor: isMine ? tint : "var(--arc-bg)", borderColor: isMine ? accent : "var(--arc-border)" }}>
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold" style={{ backgroundColor: i === 0 ? accent : "var(--arc-border)", color: i === 0 ? (quiz.encyclopedia === "zyfley" ? "#fff" : "var(--ghoulax-fg)") : "var(--arc-text-2)" }}>
                        {i === 0 ? <Crown size={13} /> : i + 1}
                      </span>
                      <span className="truncate text-sm font-semibold">{e.name}{isMine ? " (you)" : ""}</span>
                    </div>
                    <span className="shrink-0 text-xs" style={{ color: "var(--arc-text-2)" }}>Lvl {e.level} · {e.level_name}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
